import utils from '@bigcommerce/stencil-utils';
import modalFactory, { showAlertModal } from './modal';
import openSlideCart from '../common/open-slide-cart';
import { getCartPageOvrPricing } from '../custom/shipping/ovr-shipping';
import { initListingClickTracking } from '../custom/event/listing-click-analytics';
import { fireAddToCart, fireListingPageView } from '../custom/event/ga4-events';
import { syncCategoryItemListFromSearchSpring } from '../custom/schema/listing-itemlist-updater';
import { fetchAndCacheProducts, getMotherShipSku } from '../custom/event/productSkuMap';
import { getRecommenderContext } from '../custom/event/recommender-context';

// SearchSpring preserves the product-grid <ul> across paginations, so the
// MutationObserver below must be torn down on each re-entry — otherwise N
// observers accumulate and each grid mutation fires initListingClickTracking N times.
let _cardGridObserver = null;
let _matchedSwatchObserver = null;
// processProductCard re-runs destructively on every pass, but the article.card element itself
// survives, so the revert listener is bound once and must read the CURRENT initial variant from
// here instead of closing over a stale one.
const _cardRevertState = new WeakMap();

export default class CategoryListing {

    constructor(context) {
        this.context = context;
        this.myToken = context.authorization;
        this.listingPagesLoaded = this.listingPagesLoaded.bind(this);
        this.searchChooseOptionsList = new Set(context?.themeSettings?.searchChooseOptionsList);
        this.template = context.template;
        //this.previewModal = modalFactory('#previewModal')[0];
        //this.quickviewModal = modalFactory('#modal')[0];
    }

    listingPagesLoaded(options = {}) {
        const { scope = document, cardProcessingOnly = false } = options;

        // Declared BEFORE the cardProcessingOnly early return: processProductCard
        // (reached via the scoped processAllCards path below) closes over
        // searchChooseOptionsList; leaving it after the return leaves it in its
        // temporal dead zone on the scoped path → runtime ReferenceError.
        const searchChooseOptionsList = this.searchChooseOptionsList;

        // Scoped card-processing-only path (e.g. PDP "Related Products" carousel):
        // process just the cards within `scope`, skipping all page-once work —
        // analytics, reviews.io, ShopperApproved, and the page-level observers.
        if (cardProcessingOnly) {
            if (this.context.themeSettings.enable_listingPage_swatches !== true) return;
            const scopedIds = getPageProductIds(scope);
            if (!scopedIds || scopedIds.length === 0) return;
            const scopedToken = this.myToken;
            (async () => {
                try {
                    const entityIds = scopedIds.map(r => parseInt(r.productId, 10)).filter(id => !isNaN(id));
                    if (entityIds.length === 0) return;
                    const motherShipList = await fetchAndCacheProducts(entityIds, scopedToken);
                    const productMap = new Map((motherShipList || []).map(p => [p.motherShipSku, p]));
                    processAllCards(scope, productMap);
                } catch (error) {
                    console.error('listingPagesLoaded cardProcessingOnly failed:', error);
                }
            })();
            return;
        }

        fireListingPageView();
        syncCategoryItemListFromSearchSpring();
        const arrayProductIds = getPageProductIds();
        const tokenVar = this.myToken;
        const previewModal = this.previewModal;
        const quickviewModal = this.quickviewModal;
        const template = this.template;
        const enable_listingPage_swatches = this.context.themeSettings.enable_listingPage_swatches; 
        async function main(myProductIds, tok, template) {
            try {
                if(enable_listingPage_swatches === true){
                    const motherShipList = await fetchAndCacheProducts(myProductIds, tok);
                    const updatedMotherShipList = findAndApplySearchTermVariant(motherShipList);
                    const productMap = new Map(updatedMotherShipList.map(p => [p.motherShipSku, p]));
                    initializeCardProcessing(productMap);
                }
                else{
                    initListingClickTracking()
                };


                saLoadScriptStart();
            } catch (error) {
                console.error("An error occurred during execution:", error);
            }
        }
        function updateSearchSpringRecommenderClasses() {
            const star_containers = document.querySelectorAll("article > article > div.card-section.card-section--body.ss__result__details > a");
            star_containers.forEach(el => {
                el.classList.forEach(cls => {
                    if (cls !== 'star_container' && cls.endsWith('BC')) {
                        el.classList.remove(cls);
                        el.classList.add(cls.slice(0, -2));
                    }
                });
            });
        }
        updateSearchSpringRecommenderClasses();
        function saLoadScriptStart() {
            if (typeof saLoadScript === "function") {
                saLoadScript(`//www.shopperapproved.com/widgets/group2.0/${siteId}.js`);
            };
        }
        const siteId = this.context.themeSettings.shopper_approved_siteId;
        const enable_shopper_approved = this.context.themeSettings.enable_shopper_approved;
        const enable_reviews_io = this.context.themeSettings.enable_reviews_io;
        const reviews_io_siteId = this.context.themeSettings.reviews_io_storeId;
        if (siteId != null) loadReviewStars();
        if (arrayProductIds && arrayProductIds.length > 0) {
            getSwatchData(arrayProductIds);
        } else {
            const productIds = getStoredSearchData();
            if (productIds && productIds.length > 0) {
                getSwatchData(productIds);
            } else if (!enable_listingPage_swatches) {
                // Swatches disabled: initListingClickTracking() needs no product IDs.
                // Call it directly so GA4 view_item_list and click tracking fire
                // on first-time visitors (before SS has populated its localStorage cache).
                initListingClickTracking();
            }
        }

        function getPageProductIds(scope = document) {
            const productIdArray = [];

            // Select product buttons with data-product-id attribute in different selectors
            const productButtons1 = scope.querySelectorAll('article.card .card-section--buttons > form [data-product-id]');
            const productButtons2 = scope.querySelectorAll('article.card .card-section--buttons > a[data-product-id]');
            const productRecomenderButtons = scope.querySelectorAll("article > div.card-section-rightWrapper > div.card-section-rightWrapper--inner > div.card-section--buttons > a");

            // Convert NodeList to Array and map each button to an object with productId
            [...productButtons1, ...productButtons2, ...productRecomenderButtons].forEach(nodelist => {
                nodelist.forEach(button => {
                    if (button && button.dataset && button.dataset.productId) {
                        productIdArray.push({ productId: button.dataset.productId });
                    } else {
                        console.error('Button is undefined or missing dataset.productId:', button);
                    }
                });
            });

            return productIdArray;
        }


        function getSearchTermsFromQuery() {
            const params = new URLSearchParams(window.location.search);
            const terms = new Set();
            for (const [key, value] of params.entries()) {
                if (key.startsWith('filter.')) terms.add(decodeURIComponent(value).toLowerCase());
            }
            if (terms.size > 0) return Array.from(terms);
            for (const [key, value] of params.entries()) {
                if (key.startsWith('Color')){ terms.add(decodeURIComponent(value).toLowerCase())};
            }
            if (terms.size > 0) return Array.from(terms);
            const fallbackQuery = params.get('fallbackQuery');
            if (fallbackQuery) {
                decodeURIComponent(fallbackQuery).split(' ').forEach(term => terms.add(term.toLowerCase()));
                return Array.from(terms);
            }
            const query = params.get('search_query');
            if (query) {
                decodeURIComponent(query).split(' ').forEach(term => terms.add(term.toLowerCase()));
                return Array.from(terms);
            }
            return [];
        }
        function findAndApplySearchTermVariant(motherShipList) {
            if (!motherShipList) return [];
            const searchTerms = new Set(getSearchTermsFromQuery());
            if (searchTerms.size === 0) return motherShipList;
            return motherShipList.map(product => {
                if (!product.defaultSwatchList || product.defaultSwatchList.length === 0) return product;
                const matchedVariant = product.defaultSwatchList.find(variant => searchTerms.has(variant.variantSwatchName.toLowerCase()));
                if (matchedVariant) return { ...product, matchedVariant };
                return product;
            });
        }
        function loadReviewStars() {
            const star_containers = document.querySelectorAll("a.star_container");

            star_containers.forEach(el => {
                //console.log("loadReviewStars: ", el);
                el.classList.forEach(cls => {
                    //console.log("loadReviewStars: ", cls);
                    if (cls !== 'star_container' && cls.endsWith('BC')) {
                        el.classList.remove(cls);
                        el.classList.add(cls.slice(0, -2));

                        // If Review.io is enabled, insert the Review.io stars container after the star_container
                        
                    }
                    if ( enable_reviews_io === true) {

                        if (cls !== 'star_container') {
                            let skuIo = cls;
                            if(cls.endsWith('BC')){
                                skuIo = cls.slice(0, -2); // Remove 'BC' suffix
                            }


                            // Check if Review.io container already exists
                            const existingContainer = el.parentElement.querySelector(`#review_io_container_${skuIo}`);
                            if (!existingContainer) {                                
                                const reviewIoContainer = document.createElement('div');
                                reviewIoContainer.id = `review_io_container_${skuIo}`;
                                reviewIoContainer.className = 'ruk_rating_snippet';
                                reviewIoContainer.setAttribute('data-sku', skuIo);

                                el.insertAdjacentElement('beforebegin', reviewIoContainer);
                            }
                        }

                            
                        }
                });
            });
            // After all containers are processed, initialize Review.io widget
            if (enable_reviews_io === true) {
                if (window.ratingSnippet && typeof window.ratingSnippet === 'function') {
                    window.ratingSnippet("ruk_rating_snippet", {
                          store: reviews_io_siteId,
                        mode: "default",
                        color: "#EB7100",
                        linebreak: false,
                        text: "Reviews",
                        singularText: "Review",
                        lang: "en",
                        usePolaris: true,
                        showEmptyStars: false,
                        showNumericalAverageRating: false,
                    });
                    console.debug("Review.io widget initialized for listing page");
                } else {
                    console.warn("ratingSnippet function not available on window object");
                }
            }
        }
        function filterEmptyFilesFromForm(formData) {
            try {
                for (const [key, val] of formData) {
                    if (val instanceof File && !val.name && !val.size) {
                        formData.delete(key);
                    }
                }
            } catch (e) {
                console.error(e); // eslint-disable-line no-console
            }
            return formData;
        }

        function showMessageBox(message, delay) {
            //console.log("triggered", message);
            const $messageBox = $('.productAttributes-message');

            if (message) {
                $('.alertBox-message', $messageBox).text(message);
                $messageBox.show();
                if (delay) {
                    clearTimeout(this.messageBoxTimer);
                    this.messageBoxTimer = setTimeout(() => {
                        $messageBox.hide();
                    }, delay);
                }
            } else {
                $messageBox.hide();
            }
        }

        function updateCardState(elements, variant) {
            const { skuElement, imageElement, dynamicPriceContainer, lowStockCount } = elements;
            if (skuElement) {
                let displaySku = variant.variantSku;
                if (displaySku.endsWith('BC')) displaySku = displaySku.slice(0, -2);
                skuElement.textContent = `SKU: ${displaySku}`;
            }
            if (imageElement && variant.variantProductImage) {
                imageElement.src = variant.variantProductImage;
                imageElement.srcset = variant.variantProductImage;
            }
            if (dynamicPriceContainer) {
                let newPriceHtml = '';
                const isSale = variant.variantSalePrice > 0 && variant.variantRetailPrice > variant.variantSalePrice;
                if (isSale) newPriceHtml += `<div class="buy-section-price price-section"><div class="price-section--label price--sale-label">Was: </div><div class="price-section--wrapper"><div class="price-section--withoutTax non-sale-price--withoutTax"><span class="price price--non-sale">$${variant.variantRetailPrice.toFixed(2)}</span></div></div></div>`;
                newPriceHtml += `<div class="price-section"><div class="price-section--wrapper"><div class="price-section--withoutTax price-section--primary"><span class="price price--withoutTax price-primary">${variant.variantFormattedPrice}</span></div></div></div>`;
                dynamicPriceContainer.innerHTML = newPriceHtml;
            }
            if (lowStockCount) {
                lowStockCount.style.display = 'none';
            }
        }
        function buildVariantOptionSelectors(product) {

            //console.log("buildVariantOptionSelectors: ", product);
            const options = { variantOptionId: product?.variantOptionId, variantOptionValueId: product?.variantOptionValueId, variantSwatchName: product?.variantSwatchName };

            if (!options || !options.variantOptionId || !options.variantOptionValueId) {
                return null; // No options to build
            }

            const optionsContainer = document.createElement('div');

            optionsContainer.id = `data-product-option-change-${product.productId}`;
            optionsContainer.setAttribute('data-product-option-change', '');
            optionsContainer.style.display = 'none';

            // removed loop of product options untill a time where there are more options 
            const optionWrapper = document.createElement('div');
            optionWrapper.classList.add('form-field');

            optionWrapper.setAttribute('data-product-attribute', 'radio');

            const mainLabel = document.createElement('label');
            mainLabel.classList.add('form-label', 'form-label--alternate', 'form-label--inlineSmall');


            const initialLabelText = options.variantSwatchName || '';

            mainLabel.innerHTML = `
            ${options.variantSwatchName}:
            <span data-option-value="">${initialLabelText}</span>
            <small class="required-label">Required</small>
        `;
            optionWrapper.appendChild(mainLabel);

            // Create the radio inputs for each value removed this untill a time where there are more options 
            //option.forEach(({ node: value }, index) => {
            const optionId = options.variantOptionId;
            const valueId = options.variantOptionValueId;

            const inputId = `acc_attribute_radio_${optionId}_${valueId}`;

            const input = document.createElement('input');
            input.classList.add('form-radio');
            input.type = 'radio';
            input.name = `attribute[${optionId}]`;
            input.id = inputId;
            input.value = valueId;
            input.required = true;

            // Set the first option as checked by default
            //if (index === 0) {
            input.checked = true;
            //}

            const valueLabel = document.createElement('label');
            valueLabel.classList.add('form-option');
            valueLabel.setAttribute('for', inputId);
            valueLabel.setAttribute('data-product-attribute-value', valueId);

            // We create a simple text-based label since we don't have need swatch images.
            valueLabel.innerHTML = `<span class="form-option-variant">${options.variantSwatchName}</span>`;

            optionWrapper.appendChild(input);
            optionWrapper.appendChild(valueLabel);

            // Add event listener to update the main label text on change
            input.addEventListener('change', (event) => {
                if (event.target.checked) {
                    const topLabelSpan = event.target.closest('.form-field').querySelector('[data-option-value]');
                    if (topLabelSpan) {
                        topLabelSpan.textContent = options.variantSwatchName;
                    }
                }
            });
            //});

            optionsContainer.appendChild(optionWrapper);
            //});

            return optionsContainer;
        }
        function updateSlideCartWithOrv(err, responseCart) {
            const sendURL = onlineServiceUrl ?? 'https://api.storesupply.com/';
            const tru = true;
            utils.api.cart.getCart({}, (err, responseCart) => {
                if (err) {
                    // Strip the HTML from the error message
                    const tmp = document.createElement('DIV');
                    tmp.innerHTML = err;
                    tmp.innerHTML += ' (Error retrieving updated cart data)';
                    showAlertModal(tmp.textContent || tmp.innerText);
                    console.Error("Error retrieving updated cart data: ", err);
                    return;
                }

                //console.log("Listing Pages loaded getAddToCartData responseCart: ", responseCart);
                if (responseCart && responseCart.lineItems && responseCart.lineItems.physicalItems) {
                    //console.debug('productdetails cartItems: ', responseCart.lineItems.physicalItems);
                    let happens = getCartPageOvrPricing(responseCart.lineItems.physicalItems, sendURL, tru);
                }
                else {
                    //console.log("this is the else getCartPageOvrPricing");
                    let happens = getCartPageOvrPricing(null, sendURL, tru);
                }
            });

        }
        function updateSideCart(callback, responseCart) {
            const $sideCart = $('.side-cart-content');
            const $sideCartDetails = $('.side-cart-details');
            const $sideCartActions = $('.side-cart-actions');

            const options = {
                template: {
                    template: 'f/b2b/side-cart-content',
                    details: 'f/b2b/side-cart-details',
                    actions: 'f/b2b/side-cart-actions',
                    itemcount: 'f/cart/item-count',
                },
            };

            utils.api.cart.getContent(options, (err, response) => {
                if (response?.itemcount > 0) {
                   // console.log("getContent response form: ", response);
                    $sideCart.html(response.template);
                    $sideCartDetails.html(response.details);
                    $sideCartActions.html(response.actions);
                    $('.cart-quantity').html(response.itemcount);
                    itemInCart();
                }

                $('.side-cart-loading').css('display', 'none');
                $('.side-cart-item').css('opacity', '1');

                //Call the callback function if provided
                if (typeof callback === 'function') {
                    callback(err, responseCart);
                }

            });
        }

        function addProductToCart(event, form) {
            // const previewModal = modalFactory('#previewModal')[0];
            // const quickviewModal = modalFactory('#modal')[0];
            //const messageBoxTimer = setTimeout(() => { }, 0);
            const $addToCartBtn = $('#form-action-addToCart', $(event.target));
            const originalBtnVal = $addToCartBtn.val();
            const waitMessage = $addToCartBtn.data('waitMessage');

            const qtyInput = $(event.target).closest('.form').find('.form-input[name="qty[]') || 1;
            const qtyMin = parseInt(qtyInput.attr('data-quantity-min'), 10) || null;
            const qtyMax = parseInt(qtyInput.attr('data-quantity-max'), 10) || null;
            const minErr = qtyInput.attr('data-quantity-min-error') || null;
            const maxErr = qtyInput.attr('data-quantity-max-error') || null;
            const newQty = parseInt(Number(qtyInput.val()), 10) || 0;

            const articleElement = $(event.target).closest('article').find('.card-text--sku');
            const skuString = articleElement.text() || '';
            const sku = skuString?.replace(/^SKU:\s*/i, '')?.trim() || null;

            const $atcAction = $('#atc-form-action');
            $atcAction.val('add');

            // Do not do AJAX if browser doesn't support FormData
            if (window.FormData === undefined) {
                return;
            }

            // Prevent default
            event.preventDefault();
            //console.log("addProductToCart details",form);
            const recommenderContext = getRecommenderContext(form);
            const skuDataElement = form.querySelector('input[type="hidden"][name="product_id"]');
            // if(skuDataElement){
            //     const skuData = skuDataElement.value;
            //     const qtyDataElement = form.querySelector('input[id="qty[]"][name="qty[]"]');
            //     let qtyData = 1;
            //     if(qtyDataElement){
            //         qtyData  = qtyDataElement.value;
            //     }
            //     console.log("qtyInput: ", qtyInput);
            //     getAddToCartData(skuData,qtyData);
            // }

            $addToCartBtn
                .val(waitMessage)
                .prop('disabled', true);

            // Does not quality for min/max quantity
            if (newQty < qtyMin) {
                $addToCartBtn
                    .val(originalBtnVal)
                    .prop('disabled', false);

                showMessageBox(minErr, 4000);
                // swal({
                //     text: minErr,
                //     type: 'error',
                // }).catch(swal.noop);
            } else if (qtyMax > 0 && newQty > qtyMax) {
                $addToCartBtn
                    .val(originalBtnVal)
                    .prop('disabled', false);

                showMessageBox(maxErr, 4000);
                // swal({
                //     text: maxErr,
                //     type: 'error',
                // }).catch(swal.noop);
            } else if (newQty < 0 || Number.isNaN(newQty)) {
                $addToCartBtn
                    .val(originalBtnVal)
                    .prop('disabled', false);

                const invalidEntry = qtyInput.val();
                qtyInput.val(qtyMin);

                showMessageBox(`${invalidEntry} is not a valid entry`, 4000);
                // swal({
                //     text: `${invalidEntry} is not a valid entry`,
                //     type: 'error',
                // })
                //     .catch(swal.noop);
            } else {
                //this.$overlay.show();

                // Add item to cart
                utils.api.cart.itemAdd(filterEmptyFilesFromForm(new FormData(form)), (err, response) => {
                    const errorMessage = err || response.data.error;

                    //this.$overlay.hide();

                    $addToCartBtn
                        .val(originalBtnVal)
                        .prop('disabled', false);

                    // Guard statement
                    if (errorMessage) {
                        // Strip the HTML from the error message
                        const tmp = document.createElement('DIV');
                        tmp.innerHTML = errorMessage;

                        return showAlertModal(tmp.textContent || tmp.innerText);
                    }
                    let article = form.closest("article[data-original-sku-text]");
                    //if (quickviewModal) {
                    //    quickviewModal.close();
                    //}

                    // Open preview modal and update content
                    // ******* CHANGED FROM POPUP MODAL TO SLIDEOUT CART - 6.14.24 WTK ***********
                    // if (this.previewModal) {
                    //     if (this.context.addToCartMode === 'popup') {
                    //         this.previewModal.open();
                    //     }

                    //     this.updateCartContent(this.previewModal, response.data.cart_item.id);
                    // } else {
                    //     this.$overlay.show();
                    //     // if no modal, redirect to the cart page
                    //     this.redirectTo(response.data.cart_item.cart_url || this.context.urls.cart);
                    // }

                    //console.log("Listing Pages loaded getAddToCartData response: ", response)
                    getAddToCartData(response, null, newQty, sku, recommenderContext);

                    // GA4 add_to_cart — enrich with line_items data
                    if (response.data?.cart_item) {
                        const cartData = response.data;
                        const lineItem = (cartData.line_items || [])
                            .find(li => li.product_id === cartData.cart_item?.product_id)
                            || cartData.line_items?.[0]
                            || {};
                        const cardTitle = $(event.target).closest('article')
                            .find('.card-title a, .card-title').first().text().trim();
                        const enrichedCartItem = {
                            ...cartData.cart_item,
                            name:           lineItem.product_name || cartData.cart_item.name || cardTitle || '',
                            product_sku:    getMotherShipSku(cartData.cart_item?.product_id) || lineItem.sku || cartData.cart_item.sku,
                            variant_sku:    lineItem.sku || cartData.cart_item.sku,
                            sale_price:     lineItem.sale_price ?? cartData.cart_item.sale_price
                                ?? cartData.cart_item.price_ex_tax
                                ?? cartData.product_value ?? 0,
                            base_price:     lineItem.base_price ?? null,
                            retail_price:   lineItem.retail_price ?? null,
                            purchase_price: lineItem.purchase_price ?? null,
                            quantity:       lineItem.quantity ?? (newQty || 1),
                            discount:       lineItem.discount ?? 0,
                            brand_name:     lineItem.brand_name || '',
                            category_names: lineItem.category_names || [],
                            variant_id:     lineItem.variant_id ?? null,
                            currency:       lineItem.currency || cartData.currency || 'USD',
                        };
                        fireAddToCart(enrichedCartItem, enrichedCartItem.currency, recommenderContext?.recommenderName);
                    }

                    updateSideCart(updateSlideCartWithOrv, response,err);

                    //updateSideCart(previewModal, response.data.cart_item.id);


                    document.prevFocus = (document.activeElement || document.body);

                    // if (responseCart) {
                    //     console.log("Listing Pages loaded getCartPageOvrPricing responseCart.lineItems: ", responseCart.lineItems)
                    //     let happens = getCartPageOvrPricing(responseCart.lineItems.physicalItems, sendURL, tru);
                    //     console.log("Listing Pages loaded getCartPageOvrPricing happens: ", happens)
                    // }
                    // else {
                    //     console.log("this is the else getCartPageOvrPricing");
                    //     let happens = getCartPageOvrPricing(null, sendURL, tru);
                    // }
                    //console.log("Listing Pages loaded Update Side Cart called");
                    openSlideCart();
                });
            }
        }

        function getStoredSearchData() {
            const latestCachedSearch = getLatestSearchFromCache(true);
            if (!latestCachedSearch?.value?.results) return [];
            return latestCachedSearch.value.results.map(result => ({ sku: result.sku, productId: result.uid }));
        }
        function processProductCard(card, productData) {
            const skuElement = card.querySelector('.card-text--sku');
            const imageElement = card.querySelector('.card-image');
            const starsContainer = card.querySelector('.star_container');
            // :not() is load-bearing — the injected dynamic-price-container carries
            // these same classes; matching it here makes a re-pass remove it and
            // silently lose the insertion point (detached afterend = no-op).
            let originalPriceWrapper = card.querySelector('.card-text.ss__result__pricing:not(.dynamic-price-container)');
            if (originalPriceWrapper == null) {
                originalPriceWrapper = card.querySelector('div.price-section--withoutTax.price-section--primary > span[data-product-price-without-tax]');
            }
            const buttonsContainer = card.querySelector('.card-section--buttons');
            const lowStockCount = card.querySelector('.card-stock--lowStock');

            card.querySelector('.card-swatches-container')?.remove();
            card.querySelector('.dynamic-price-container')?.remove();


            if (!card.dataset.originalSkuText) card.dataset.originalSkuText = productData?.motherShipSku;

            card.dataset.product_sku = productData?.motherShipSku || '';

            const variantForInitialDisplay = productData.matchedVariant || productData.defaultSwatchList.find(v => v.variantIsDefault) || productData.defaultSwatchList[0];
            //console.log("variantForInitialDisplay: ", variantForInitialDisplay);
            const swatchesContainer = document.createElement('div');
            swatchesContainer.className = 'card-swatches-container';
            const dynamicPriceContainer = document.createElement('div');
            dynamicPriceContainer.className = 'card-text ss__result__pricing dynamic-price-container';
            const cardElements = { skuElement, imageElement, dynamicPriceContainer, lowStockCount };

            productData.defaultSwatchList.forEach(variant => {
                if (!variant.variantSwatchImage) return;
                const swatchLink = document.createElement('a');
                swatchLink.href = variant.variantURL;
                swatchLink.title = variant.variantSwatchName;
                swatchLink.className = 'card-swatch-item';
                if (variant === productData.matchedVariant) swatchLink.classList.add('card-swatch-item--matched');
                if (variant.variantIsDefault) swatchLink.classList.add('card-swatch-item--default-variant');
                const swatchImage = document.createElement('span');
                swatchImage.className = 'card-swatch-image';
                swatchImage.style.backgroundImage = `url(${variant.variantSwatchImage})`;
                swatchLink.appendChild(swatchImage);
                swatchLink.addEventListener('mouseenter', () => updateCardState(cardElements, variant));
                swatchesContainer.appendChild(swatchLink);
            });
            // Revert scope is the whole CARD, not the swatch container. The favorites star lives
            // in .card-figure, a different subtree from the swatches, so reverting when the
            // pointer left the container made a hovered variant impossible to favorite: the card
            // snapped back before the pointer reached the star. This also matches how
            // SearchSpring's own variant swatches behave, where the selection persists.
            // The synthetic mouseleave in handleIntersection is dispatched with bubbles:true, so
            // it still reaches this listener.
            _cardRevertState.set(card, { cardElements, variantForInitialDisplay });
            if (!card.dataset.swatchRevertBound) {
                card.dataset.swatchRevertBound = '1';
                card.addEventListener('mouseleave', () => {
                    const state = _cardRevertState.get(card);
                    if (state) updateCardState(state.cardElements, state.variantForInitialDisplay);
                });
            }

            if (originalPriceWrapper) originalPriceWrapper.style.display = 'none';
            // The injected container re-renders the FULL price display (Was + Now).
            // Theme cards render MSRP/"Was:" as SIBLING price-sections that hiding
            // the primary span alone leaves visible — a server-marked-on-sale item
            // then shows a doubled "Was:" row. Hide every server price-section
            // except the primary one that hosts the injected container. SS grid
            // cards have no .card-text--price ancestor → no-op there (their whole
            // pricing block IS originalPriceWrapper, already hidden above).
            const serverPriceBlock = originalPriceWrapper ? originalPriceWrapper.closest('.card-text--price') : null;
            if (serverPriceBlock) {
                serverPriceBlock.querySelectorAll(':scope > .price-section').forEach(section => {
                    if (!section.querySelector('.price-section--primary')) section.style.display = 'none';
                });
            }
            if (originalPriceWrapper) originalPriceWrapper.insertAdjacentElement('afterend', dynamicPriceContainer);
            // star_container is ShopperApproved markup — absent from theme-rendered
            // cards now that enable_shopper_approved is off (SA decommissioning).
            // Fall back to the reviews.io snippet, then the SKU line (always
            // present: processing requires .card-text--sku).
            const swatchAnchor = starsContainer || card.querySelector('.ruk_rating_snippet') || skuElement;
            if (swatchAnchor) swatchAnchor.insertAdjacentElement('afterend', swatchesContainer);
            if (buttonsContainer && variantForInitialDisplay) {
                // const imageLink = card.querySelector('.ss__image__link');
                // if (imageLink) {
                //     imageLink.href = variantForInitialDisplay.variantURL;
                // }
                const existsInList = searchChooseOptionsList.has(productData.productId);
                if (!existsInList) {
                    if (buttonsContainer) buttonsContainer.innerHTML = '';
                    const cardTitle = card.querySelector('.card-title a, .card-title');
                    const productName = cardTitle ? cardTitle.textContent.trim() : '';
                    const form = document.createElement('form');
                    form.className = 'form form-cardAdd';
                    form.method = 'post';
                    form.action = '/cart.php';
                    form.setAttribute('aria-label', `Add ${productName} to cart`);
                    form.innerHTML = `<input type="hidden" name="action" value="add"><input type="hidden" name="product_id" value="${productData.productId}"><input class="form-input form-input-qty" name="qty[]" type="tel" value="1" min="1" aria-label="Quantity for ${productName}"><input class="button button--primary button--cardAdd" type="submit" ss-track-cart-add="${productData.productId}" value="Add to Cart" aria-label="Add to Cart: ${productName}">`;
                    form.addEventListener('submit', (event) => {
                        addProductToCart(event, form);
                    });
                    const optionSelectors = buildVariantOptionSelectors(variantForInitialDisplay);
                    if (optionSelectors) {
                        form.appendChild(optionSelectors);
                    }

                    buttonsContainer.appendChild(form);
                }

            }

            updateCardState(cardElements, variantForInitialDisplay);
        }
        // Idempotent, container-scoped card processor. Pure DOM — no analytics,
        // no third-party, no observers. Reused by the grid path and the scoped
        // cardProcessingOnly path (PDP Related Products carousel).
        function processAllCards(scope = document, productMap) {
            if (!productMap) return;
            scope.querySelectorAll('article.card').forEach(card => {
                const skuElement = card.querySelector('.card-text--sku');
                if (skuElement) {
                    let onPageSku = card.dataset.originalSkuText ? card.dataset.originalSkuText.replace('SKU:', '').trim() : skuElement.textContent.replace('SKU:', '').trim();
                    if (onPageSku.includes('SKU:')) {
                        onPageSku = onPageSku.substring(onPageSku.lastIndexOf('SKU:') + 4);
                    }
                    const productData = productMap.get(onPageSku);
                    if (productData && productData.defaultSwatchList.length > 0) {
                        processProductCard(card, productData);
                    }
                }
            });
        }

        function initializeCardProcessing(productMap) {
            let debounceTimer;
            processAllCards(document, productMap);
            initListingClickTracking();
            const productGrid = document.querySelector("#ss__content--search > div > ul");
            if (!productGrid) return;
            if (_cardGridObserver) _cardGridObserver.disconnect();
            _cardGridObserver = new MutationObserver(() => {
                clearTimeout(debounceTimer);
                // Re-passes serve the SearchSpring-owned grid only: SS re-renders wipe
                // its cards, so they need reprocessing. Theme-rendered cards (footer
                // recommender carousel) are never externally re-rendered — they get
                // the one initial full-document pass and must not be swept again.
                debounceTimer = setTimeout(() => { processAllCards(productGrid, productMap); initListingClickTracking(); }, 150);
            });
            _cardGridObserver.observe(productGrid, { childList: true });
        }

        function getSwatchData(productIds) {
            const entityIds = productIds.map(result => parseInt(result.productId, 10)).filter(id => !isNaN(id));
            if (entityIds.length === 0) return;

            main(entityIds, tokenVar, template);
        }

        function handleIntersection(entries, observer) {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const matchedSwatchElement = entry.target.querySelector('.card-swatch-item--matched');
                    if (matchedSwatchElement) {
                        matchedSwatchElement.dispatchEvent(new MouseEvent('mouseenter', {
                            bubbles: true,
                            cancelable: true
                        }));
                        matchedSwatchElement.dispatchEvent(new MouseEvent('mouseleave', {
                            bubbles: true,
                            cancelable: true
                        }));
                    }


                    observer.unobserve(entry.target);
                }
            });
        }

        if (_matchedSwatchObserver) _matchedSwatchObserver.disconnect();
        _matchedSwatchObserver = new IntersectionObserver(handleIntersection, {
            root: null,
            threshold: 0.1
        });

        document.querySelectorAll('article.card').forEach(node => {
            _matchedSwatchObserver.observe(node);
        });


        if (template === 'page/product') {

        }

        const gridToListSwitch = document.querySelector("#searchspring-toolbar > div > div.grid-list-switcher");
        if (gridToListSwitch != null && !gridToListSwitch.dataset.swatchSwitchBound) {
            gridToListSwitch.dataset.swatchSwitchBound = '1';
            gridToListSwitch.addEventListener('click', () => {
                document.querySelectorAll('article.card').forEach(node => {
                    const matchedSwatchElement = node.querySelector('.card-swatch-item--matched');
                    const defaultSwatchElement = node.querySelector('.card-swatch-item--default-variant');
                    if (matchedSwatchElement) {
                        matchedSwatchElement.dispatchEvent(new MouseEvent('mouseenter', {
                            bubbles: true,
                            cancelable: true
                        }));
                    }
                    else if (defaultSwatchElement) {
                        defaultSwatchElement.dispatchEvent(new MouseEvent('mouseenter', {
                            bubbles: true,
                            cancelable: true
                        }));
                    }
                    else {
                        const firstSwatchElement = node.querySelector('.card-swatch-item--matched');
                        if (firstSwatchElement) {
                            firstSwatchElement.dispatchEvent(new MouseEvent('mouseenter', {
                                bubbles: true,
                                cancelable: true
                            }));
                        }
                    }
                });
            });
        }

    }
}

